/**
 * The device's proof (decision 55; part 29a, tables CH and LG10; part 36a, principle C3): the device signs a challenge
 * the relay issued, for one purpose, with its non-extractable P-256 key (part 25a's `createDeviceSigningKey`); the relay
 * knows the device by the public key's SPKI. What is signed is the relay's `signedMessage`: a versioned label, the
 * purpose and the challenge, so no signature serves two purposes.
 */
import { DEVICE_SIGNATURE } from '@cockatrice/crypto';
import { toBase64Url, utf8 } from './bytes.ts';

/** What a signature is for (the relay's `Purpose`). This half signs `login`, `session`, `verify` and `factor`; from fix-next 84 `logout`. */
export type Purpose = 'login' | 'session' | 'verify' | 'enrol' | 'factor' | 'proof' | 'change' | 'reset' | 'logout';

/** The device's key as kept: the private key (never extractable) and the public key's SPKI. */
export interface DeviceKey {
  readonly privateKey: CryptoKey;
  readonly spki: Uint8Array<ArrayBuffer>;
}

/** A device's proof as a request carries it: its public key, the challenge and the signature, each base64url. */
export interface DeviceProof {
  readonly key: string;
  readonly challenge: string;
  readonly signature: string;
}

/** CH5: the bytes signed for `purpose` over `challenge`. */
export function signedMessage(purpose: Purpose, challenge: Uint8Array): Uint8Array<ArrayBuffer> {
  const label = utf8(`cockatrice device v1\n${purpose}\n`);
  const message = new Uint8Array(label.length + challenge.length);
  message.set(label);
  message.set(challenge, label.length);
  return message;
}

/** The device's proof for `purpose` over `challenge`. */
export async function proveDevice(subtle: SubtleCrypto, key: DeviceKey, purpose: Purpose, challenge: Uint8Array<ArrayBuffer>): Promise<DeviceProof> {
  const signature = new Uint8Array(await subtle.sign(DEVICE_SIGNATURE, key.privateKey, signedMessage(purpose, challenge)));
  return { key: toBase64Url(key.spki), challenge: toBase64Url(challenge), signature: toBase64Url(signature) };
}
